import { useEffect, useState } from "react";
import { site } from "../../config/site";
import { api } from "../lib/api";
import { linkProps } from "../lib/router";
import { Footer, Header, Monogram } from "../components/Layout";
import { useSession } from "../lib/auth-client";

interface PublicEvent {
  slug: string;
  name: string;
  tagline: string | null;
  startsAt: string | null;
  venueName: string | null;
}

const dayLabel = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : "";

const timeLabel = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

export function Landing() {
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const { data: session } = useSession();

  useEffect(() => {
    api
      .get<{ events: PublicEvent[] }>("/api/events")
      .then((r) => setEvents(r.events))
      .catch(() => setEvents([]));
  }, []);

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-5xl">
        <Header
          right={
            session ? (
              <a {...linkProps("/rsvp")} className="btn btn-ghost text-sm">
                Your itinerary
              </a>
            ) : (
              <a {...linkProps("/login")} className="btn btn-ghost text-sm">
                Sign in
              </a>
            )
          }
        />
      </div>

      {/* ---------------------------------------------------------------- Hero */}
      <section className="animate-rise px-6 pt-10 pb-20 text-center sm:pt-20">
        <p className="eyebrow">Together with their families</p>
        <h1 className="mx-auto mt-6 max-w-3xl font-display text-6xl leading-[1.05] font-light sm:text-8xl">
          {site.partnerOne}
          <span className="mx-3 text-muted italic">and</span>
          {site.partnerTwo}
        </h1>

        <div className="mx-auto mt-10 flex max-w-md items-center gap-5">
          <span className="h-px flex-1 bg-line" />
          <p className="text-sm tracking-widest whitespace-nowrap text-muted uppercase">
            {site.dateLabel}
          </p>
          <span className="h-px flex-1 bg-line" />
        </div>
        <p className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 font-display text-2xl text-muted italic">
          {site.locations.map((city, i) => (
            <span key={city} className="flex items-center gap-4">
              {i > 0 && <span className="text-line not-italic">&middot;</span>}
              {city}
            </span>
          ))}
        </p>

        <p className="mx-auto mt-10 max-w-xl text-lg leading-relaxed text-ink/80">
          {site.heroTagline}
        </p>

        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            {...linkProps("/signup")}
            className="btn btn-primary w-full sm:w-auto"
          >
            RSVP &amp; see your events
          </a>
          <a href="#schedule" className="btn btn-ghost w-full sm:w-auto">
            The weekend
          </a>
        </div>
      </section>

      {/* --------------------------------------------------------------- Story */}
      <section className="border-y border-line bg-paper px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">Our story</p>
          <p className="mt-6 font-display text-2xl leading-relaxed font-light sm:text-3xl">
            {site.story}
          </p>
        </div>
      </section>

      {/* ------------------------------------------------------------ Schedule */}
      <section
        id="schedule"
        className="mx-auto max-w-3xl scroll-mt-8 px-6 py-20"
      >
        <div className="text-center">
          <p className="eyebrow">The weekend</p>
          <h2 className="mt-4 font-display text-4xl font-light sm:text-5xl">
            A few days together
          </h2>
          <p className="mx-auto mt-5 max-w-md text-muted">
            Mr & Mrs Worldwide have family across the globe - so we're doing
            small events in Singapore and Germany, with the main event in
            Vancouver. The Singapore and Germany events are smaller scale so we
            only accommodate some people at all 3. RSVP to see which events
            you're invited to.
          </p>
        </div>

        <ol className="mt-14 space-y-px overflow-hidden rounded-2xl border border-line">
          {events.map((e) => (
            <li
              key={e.slug}
              className="flex flex-col gap-1 bg-paper px-6 py-6 sm:flex-row sm:items-baseline sm:justify-between"
            >
              <div>
                <h3 className="font-display text-2xl font-light">{e.name}</h3>
                {e.tagline && (
                  <p className="mt-1 text-sm text-muted">{e.tagline}</p>
                )}
              </div>
              <div className="text-sm text-muted sm:text-right">
                <p>{dayLabel(e.startsAt)}</p>
                <p className="text-ink/60">
                  {timeLabel(e.startsAt)}
                  {e.venueName ? ` · ${e.venueName}` : ""}
                </p>
              </div>
            </li>
          ))}
          {events.length === 0 && (
            <li className="bg-paper px-6 py-10 text-center text-sm text-muted">
              The schedule is being finalised.
            </li>
          )}
        </ol>

        <div className="mt-12 text-center">
          <a {...linkProps("/signup")} className="btn btn-primary">
            See my invitation
          </a>
          <p className="mt-4 text-sm text-muted">
            Kindly reply by {site.rsvpDeadlineLabel}
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-6">
        <div className="flex justify-center pb-4">
          <Monogram className="text-2xl" />
        </div>
        <Footer />
      </div>
    </div>
  );
}
